import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import payrollService from '../services/payrollService';
import { useNotification } from '../context/NotificationContext';
import { AuthContext } from '../context/AuthContext';
import ConfirmActionModal from '../components/ConfirmActionModal';
import SortableHeader from '../components/SortableHeader';
import { FREQUENCIES } from '../constants/payrollPeriods';
import { formatDate } from '../utils/datetime';
import '../styles/general.css';

// Matches the backend's PAGE_SIZE.
const PER_PAGE = 50;

// Employee, Frequency, Period, Payment Date, Period range, Total,
// Total Payable, Status, Actions.
const COLUMN_COUNT = 9;

// The statuses a payroll can still be edited in. Mirrors the backend's
// EDITABLE_STATUSES; the API rejects writes to the other two.
const EDITABLE_STATUSES = ['borrador', 'aprobado'];

const PayrollListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { canManage } = useContext(AuthContext);
  // Write actions are hidden, not disabled: a button the user can never
  // enable is its own kind of noise. The API rejects them regardless.
  const canWrite = canManage && canManage('payroll');

  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState('');
  const [frequency, setFrequency] = useState('');
  const [dateField, setDateField] = useState('payment_date');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [ordering, setOrdering] = useState('-payment_date');

  // { payroll, action } - one modal serves all three transitions.
  const [pendingAction, setPendingAction] = useState(null);
  const [processing, setProcessing] = useState(false);

  const totalPages = Math.ceil(totalCount / PER_PAGE);

  const loadPayrolls = useCallback(async () => {
    try {
      setLoading(true);
      const res = await payrollService.listPayrolls({
        search: search || null,
        frequency: frequency || null,
        dateField,
        dateFrom: dateFrom || null,
        dateTo: dateTo || null,
        showInactive,
        ordering,
        page
      });
      setPayrolls(res.data.results || []);
      setTotalCount(res.data.count || 0);
    } catch (err) {
      console.error('Error loading payrolls:', err);
      notify.error(t('payroll.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [search, frequency, dateField, dateFrom, dateTo, showInactive, ordering, page, notify, t]);

  useEffect(() => {
    loadPayrolls();
  }, [loadPayrolls]);

  const handleFilterChange = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  // SortableHeader renders the arrow but does not own the state: clicking the
  // active column flips its direction, a new column starts ascending.
  const handleSort = (field) => {
    setOrdering((current) => (current === field ? `-${field}` : field));
    setPage(1);
  };

  const handleActionConfirm = async () => {
    if (!pendingAction) return;
    const { payroll, action } = pendingAction;
    setProcessing(true);
    try {
      await payrollService[action](payroll.id);
      notify.success(t('common.updatedSuccessfully'));
      setPendingAction(null);
      loadPayrolls();
    } catch (err) {
      console.error(`Error running ${action} on payroll:`, err);
      notify.error(err.response?.data?.detail || t('common.failedToUpdate'));
    } finally {
      setProcessing(false);
    }
  };

  const confirmMessage = () => {
    if (!pendingAction) return '';
    return {
      approve: t('payroll.confirmApprove'),
      pay: t('payroll.confirmPay'),
      cancel: t('payroll.confirmCancel'),
    }[pendingAction.action];
  };

  if (loading && payrolls.length === 0) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="page-container-wide">
      <div className="page-header">
        <h1>{t('payroll.title')}</h1>
        {canWrite && (
          <button onClick={() => navigate('/payroll/create')} className="btn btn-primary">
            {t('payroll.createButton')}
          </button>
        )}
      </div>

      <div className="filter-panel">
        <div className="filter-row">
          <div className="filter-group">
            <label className="form-label" htmlFor="payroll-search">
              {t('filters.searchValue')}
            </label>
            <input
              id="payroll-search"
              type="text"
              value={search}
              onChange={(e) => handleFilterChange(setSearch)(e.target.value)}
              placeholder={t('payroll.searchPlaceholder')}
              className="form-input"
            />
          </div>

          <div className="filter-group">
            <label className="form-label" htmlFor="payroll-frequency">
              {t('payroll.frequency')}
            </label>
            <select
              id="payroll-frequency"
              value={frequency}
              onChange={(e) => handleFilterChange(setFrequency)(e.target.value)}
              className="form-select"
            >
              <option value="">{t('filters.none')}</option>
              {FREQUENCIES.map((value) => (
                <option key={value} value={value}>
                  {t(`payroll.frequencies.${value}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="form-label" htmlFor="payroll-date-field">
              {t('payroll.dateFieldLabel')}
            </label>
            <select
              id="payroll-date-field"
              value={dateField}
              onChange={(e) => handleFilterChange(setDateField)(e.target.value)}
              className="form-select"
            >
              <option value="payment_date">{t('payroll.dateFieldPaymentDate')}</option>
              <option value="period">{t('payroll.dateFieldPeriod')}</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="form-label" htmlFor="payroll-date-from">
              {t('filters.dateFrom')}
            </label>
            <input
              id="payroll-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => handleFilterChange(setDateFrom)(e.target.value)}
              className="form-input"
            />
          </div>

          <div className="filter-group">
            <label className="form-label" htmlFor="payroll-date-to">
              {t('filters.dateTo')}
            </label>
            <input
              id="payroll-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => handleFilterChange(setDateTo)(e.target.value)}
              className="form-input"
            />
          </div>

          <div className="filter-group filter-group-checkbox">
            <label className="form-label" htmlFor="payroll-show-inactive">
              <input
                id="payroll-show-inactive"
                type="checkbox"
                checked={showInactive}
                onChange={(e) => handleFilterChange(setShowInactive)(e.target.checked)}
              />
              {' '}{t('filters.showInactive')}
            </label>
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="table">
          <thead>
            <tr>
              <SortableHeader
                field="employee_name_search"
                label={t('payroll.employee')}
                sortBy={ordering}
                onSort={handleSort}
              />
              <th>{t('payroll.frequency')}</th>
              <SortableHeader
                field="period_code"
                label={t('payroll.period')}
                sortBy={ordering}
                onSort={handleSort}
              />
              <SortableHeader
                field="payment_date"
                label={t('payroll.paymentDate')}
                sortBy={ordering}
                onSort={handleSort}
              />
              <th>{t('payroll.periodStart')} / {t('payroll.periodEnd')}</th>
              <th className="text-right">{t('payroll.total')}</th>
              <th className="text-right">{t('payroll.totalPayable')}</th>
              <th>{t('common.status')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {payrolls.length === 0 ? (
              <tr>
                <td colSpan={COLUMN_COUNT} className="empty-state">
                  <p>{t('payroll.noPayrolls')}</p>
                </td>
              </tr>
            ) : (
              payrolls.map((payroll) => (
                <tr
                  key={payroll.id}
                  className={payroll.status === 'cancelado' ? 'table-row-cancelled' : ''}
                >
                  <td>{payroll.employee_name}</td>
                  <td>{t(`payroll.frequencies.${payroll.frequency}`)}</td>
                  <td>{payroll.period_code}</td>
                  {/* All three are DateField values: formatDate does not
                      convert, so the day never shifts by timezone. */}
                  <td>{formatDate(payroll.payment_date)}</td>
                  <td>
                    {formatDate(payroll.period_start)} / {formatDate(payroll.period_end)}
                  </td>
                  <td className="text-right">{payroll.total_earnings}</td>
                  <td className="text-right">{payroll.total_payable}</td>
                  <td>
                    <span className={`table-status ${payroll.status}`}>
                      {t(`payroll.status.${payroll.status}`)}
                    </span>
                  </td>
                  <td className="table-actions">
                    {canWrite && payroll.status === 'borrador' && (
                      <button
                        onClick={() => setPendingAction({ payroll, action: 'approve' })}
                        className="btn btn-success btn-small"
                      >
                        {t('payroll.approve')}
                      </button>
                    )}
                    {canWrite && payroll.status === 'aprobado' && (
                      <button
                        onClick={() => setPendingAction({ payroll, action: 'pay' })}
                        className="btn btn-success btn-small"
                      >
                        {t('payroll.pay')}
                      </button>
                    )}
                    {canWrite && EDITABLE_STATUSES.includes(payroll.status) && (
                      <button
                        onClick={() => navigate(`/payroll/${payroll.id}/edit`)}
                        className="btn btn-primary btn-small"
                      >
                        {t('common.edit')}
                      </button>
                    )}
                    {canWrite && EDITABLE_STATUSES.includes(payroll.status) && (
                      <button
                        onClick={() => setPendingAction({ payroll, action: 'cancel' })}
                        className="btn btn-danger btn-small"
                      >
                        {t('common.cancel')}
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/payroll/${payroll.id}`)}
                      className="btn btn-info btn-small"
                    >
                      {t('payroll.view')}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="btn btn-primary"
          >
            {t('common.previous')}
          </button>
          <span className="pagination-info">{t('common.page')} {page}</span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="btn btn-primary"
          >
            {t('common.next')}
          </button>
        </div>
      )}

      <ConfirmActionModal
        isOpen={!!pendingAction}
        onClose={() => setPendingAction(null)}
        onConfirm={handleActionConfirm}
        title={t('payroll.title')}
        message={confirmMessage()}
        confirmLabel={t('common.confirm')}
        confirming={processing}
      />
    </div>
  );
};

export default PayrollListPage;
